// src/pages/subscriptions/SubscriptionsPage.tsx
// Раздел «Абонементы»: годовое членство клуба со скидкой на билеты.
import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import ConfirmModal from "../../components/ui/ConfirmModal";
import AddSubscriptionModal from "../../components/subscriptions/AddSubscriptionModal";
import SubscriptionOrdersModal from "../../components/subscriptions/SubscriptionOrdersModal";
import {
    adminCreateSubscriptionRequest,
    annulSubscriptionRequest,
    downloadSubscriptionInvoicePdfRequest,
    getSubscriptionsRequest,
    sendSubscriptionInvoiceRequest,
    sendSubscriptionPaymentLinkRequest,
    updateSubscriptionRequest,
} from "../../services/api/subscriptions.api";
import {
    AdminCreateSubscriptionPayload,
    SubscriptionItem,
    SubscriptionStatus,
} from "../../services/types/subscriptions.types";
import { CheckCircle2, FileText, Link2, ListOrdered, Send, XCircle } from "lucide-react";

const PAGE_SIZE = 20;

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
    unpaid: "не оплачено",
    active: "активно и оплачено",
    expired: "истекло",
};

// Цвета статусов подобраны в тон бейджам страницы заказов.
const STATUS_COLORS: Record<SubscriptionStatus, string> = {
    unpaid: "#f59e0b",
    active: "#16a34a",
    expired: "#de7878",
};

const formatDate = (value?: string | null) => {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "—";
    }

    return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const formatMoney = (value?: string | number | null) => {
    const numeric = Number(value ?? 0);

    if (Number.isNaN(numeric)) {
        return "0.00 €";
    }

    return `${numeric.toFixed(2)} €`;
};

const formatPercent = (value?: string | number | null) => {
    const numeric = Number(value ?? 0);

    if (Number.isNaN(numeric)) {
        return "0 %";
    }

    // Целые проценты показываем без лишних нулей: 10 %, а не 10.00 %.
    return Number.isInteger(numeric) ? `${numeric} %` : `${numeric.toFixed(2)} %`;
};

const buildCustomerName = (item: SubscriptionItem) => {
    const info = item.customer_info;

    if (!info) {
        return "Клиент не найден";
    }

    const name = [info.first_name, info.last_name].filter(Boolean).join(" ").trim();

    return name || "Без имени";
};

const SubscriptionsPage: React.FC = () => {
    const [items, setItems] = useState<SubscriptionItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(0);

    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | "">("");
    // Фильтр по дате оформления абонемента (ГГГГ-ММ-ДД).
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    // С какого сайта куплен абонемент: все / основной / второй сайт (UA).
    const [siteSourceFilter, setSiteSourceFilter] = useState<"" | "main" | "ua">("");

    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    // id абонемента, по которому сейчас выполняется действие — блокирует его иконки
    const [busyId, setBusyId] = useState<string | null>(null);

    const [isAddOpen, setIsAddOpen] = useState(false);
    const [itemToAnnul, setItemToAnnul] = useState<SubscriptionItem | null>(null);
    const [itemToMarkPaid, setItemToMarkPaid] = useState<SubscriptionItem | null>(null);
    // Окно «Заказы клиента» (только просмотр).
    const [itemOrders, setItemOrders] = useState<SubscriptionItem | null>(null);

    // Поиск не отправляем на каждый символ.
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearch(searchValue);
        }, 400);

        return () => window.clearTimeout(timer);
    }, [searchValue]);

    // Смена фильтров всегда возвращает на первую страницу.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, statusFilter, dateFrom, dateTo, siteSourceFilter]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getSubscriptionsRequest(page, PAGE_SIZE, {
                search: debouncedSearch,
                status: statusFilter,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                site_source: siteSourceFilter || undefined,
            });

            setItems(response.items);
            setTotalSize(response.total_size);
        } catch (error) {
            setPageError(
                error instanceof Error
                    ? error.message
                    : "Не удалось загрузить абонементы.",
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, [page, debouncedSearch, statusFilter, dateFrom, dateTo, siteSourceFilter]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(totalSize / PAGE_SIZE)),
        [totalSize],
    );

    const replaceItem = (updated: SubscriptionItem) => {
        setItems((prev) =>
            prev.map((item) => (item.id === updated.id ? updated : item)),
        );
    };

    const runAction = async (
        item: SubscriptionItem,
        action: () => Promise<void>,
    ) => {
        setBusyId(item.id);
        setActionError(null);
        setActionMessage(null);

        try {
            await action();
        } catch (error) {
            setActionError(
                error instanceof Error ? error.message : "Не удалось выполнить действие.",
            );
        } finally {
            setBusyId(null);
        }
    };

    const handleSendInvoice = (item: SubscriptionItem) =>
        runAction(item, async () => {
            await sendSubscriptionInvoiceRequest(item.id);
            setActionMessage(
                `Письмо по абонементу ${item.subscription_number} отправлено на ${
                    item.customer_info?.email ?? "почту клиента"
                }.`,
            );
            // Бэкенд выставляет invoice_sent и может присвоить номер счёта — перечитываем.
            await loadData();
        });

    const handleSendPaymentLink = (item: SubscriptionItem) =>
        runAction(item, async () => {
            await sendSubscriptionPaymentLinkRequest(item.id);
            setActionMessage(
                `Ссылка на оплату по абонементу ${item.subscription_number} отправлена на ${
                    item.customer_info?.email ?? "почту клиента"
                }.`,
            );
        });

    const handleDownloadPdf = (item: SubscriptionItem) =>
        runAction(item, async () => {
            const { blob, filename } = await downloadSubscriptionInvoicePdfRequest(
                item.id,
            );

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        });

    const handleConfirmMarkPaid = async () => {
        if (!itemToMarkPaid) {
            return;
        }

        const target = itemToMarkPaid;
        setItemToMarkPaid(null);

        await runAction(target, async () => {
            const updated = await updateSubscriptionRequest(target.id, {
                is_paid: true,
            });
            replaceItem(updated);
            setActionMessage(
                `Абонемент ${updated.subscription_number} активирован до ${formatDate(
                    updated.ends_at,
                )}.`,
            );
        });
    };

    const handleConfirmAnnul = async () => {
        if (!itemToAnnul) {
            return;
        }

        const target = itemToAnnul;
        setItemToAnnul(null);

        await runAction(target, async () => {
            const updated = await annulSubscriptionRequest(target.id);
            replaceItem(updated);
            setActionMessage(
                `Абонемент ${updated.subscription_number} аннулирован, скидка снята.`,
            );
        });
    };

    const handleCreate = async (payload: AdminCreateSubscriptionPayload) => {
        const created = await adminCreateSubscriptionRequest(payload);
        setIsAddOpen(false);
        setActionError(null);
        setActionMessage(
            created.is_paid
                ? `Абонемент ${created.subscription_number} выдан и активен до ${formatDate(
                      created.ends_at,
                  )}.`
                : `Абонемент ${created.subscription_number} создан со статусом «не оплачено».`,
        );
        setPage(1);
        await loadData();
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Абонементы"
                description="Годовое членство клуба: скидка на любой билет в течение года."
                actions={
                    <Button type="button" onClick={() => setIsAddOpen(true)}>
                        Выдать абонемент
                    </Button>
                }
            />

            <Card>
                <section>
                    <form
                        className="orders-filter-bar"
                        onSubmit={(event) => event.preventDefault()}
                    >
                        <div className="orders-filter-row">
                            <Field label="Поиск" htmlFor="subscription-search">
                                <Input
                                    id="subscription-search"
                                    value={searchValue}
                                    placeholder="Номер, email, имя, фамилия или город"
                                    onChange={(event) => setSearchValue(event.target.value)}
                                />
                            </Field>

                            <Field label="Статус" htmlFor="subscription-status">
                                <select
                                    id="subscription-status"
                                    className="ui-select"
                                    value={statusFilter}
                                    onChange={(event) =>
                                        setStatusFilter(
                                            event.target.value as SubscriptionStatus | "",
                                        )
                                    }
                                >
                                    <option value="">Все статусы</option>
                                    <option value="unpaid">Не оплачено</option>
                                    <option value="active">Активно и оплачено</option>
                                    <option value="expired">Истекло</option>
                                </select>
                            </Field>

                            <Field label="Сайт" htmlFor="subscription-site-source">
                                <select
                                    id="subscription-site-source"
                                    className="ui-select"
                                    value={siteSourceFilter}
                                    onChange={(event) =>
                                        setSiteSourceFilter(event.target.value as "" | "main" | "ua")
                                    }
                                >
                                    <option value="">Все</option>
                                    <option value="main">Основной</option>
                                    <option value="ua">UA-сайт</option>
                                </select>
                            </Field>

                            <Field label="Дата оформления от" htmlFor="subscription-date-from">
                                <Input
                                    id="subscription-date-from"
                                    type="date"
                                    value={dateFrom}
                                    max={dateTo || undefined}
                                    onChange={(event) => setDateFrom(event.target.value)}
                                />
                            </Field>

                            <Field label="Дата оформления до" htmlFor="subscription-date-to">
                                <Input
                                    id="subscription-date-to"
                                    type="date"
                                    value={dateTo}
                                    min={dateFrom || undefined}
                                    onChange={(event) => setDateTo(event.target.value)}
                                />
                            </Field>
                        </div>
                    </form>
                </section>

                {pageError && <div className="form-global-error">{pageError}</div>}
                {actionError && <div className="form-global-error">{actionError}</div>}
                {actionMessage && (
                    <div className="form-global-success">{actionMessage}</div>
                )}

                {isLoading ? (
                    <div className="table-skeleton">
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="empty-block">
                        <h3 className="empty-block-title">Абонементов нет</h3>
                        <p className="empty-block-text">
                            Измените фильтры или выдайте абонемент клиенту вручную.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Абонемент</th>
                                        <th>Клиент</th>
                                        <th>Город</th>
                                        <th>Срок действия</th>
                                        <th>Скидка</th>
                                        <th>Сумма</th>
                                        <th>Статус</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => {
                                        const status = item.status;
                                        const isBusy = busyId === item.id;
                                        // Отметить оплату можно только у неоплаченного счёта.
                                        const canMarkPaid = !item.is_paid && !item.is_canceled;
                                        // Аннулировать имеет смысл только действующий абонемент.
                                        const canAnnul = status === "active";

                                        return (
                                            <tr key={item.id}>
                                                <td>
                                                    <div className="table-primary-cell">
                                                        <span className="table-primary-text">
                                                            {item.subscription_number}
                                                        </span>
                                                        {item.site_source && item.site_source !== "main" ? (
                                                            <span
                                                                className="site-source-badge"
                                                                title="Абонемент куплен на втором сайте (заголовок X-Site-Source)"
                                                            >
                                                                {item.site_source.toUpperCase()}-сайт
                                                            </span>
                                                        ) : null}
                                                        <span className="table-secondary-text">
                                                            Счёт{" "}
                                                            {item.invoice_number
                                                                ? `RE-${String(
                                                                      item.invoice_number,
                                                                  ).padStart(6, "0")}`
                                                                : "не выставлен"}
                                                        </span>
                                                        <span className="table-secondary-text">
                                                            Создан {formatDate(item.created_at)}
                                                            {item.created_by_admin
                                                                ? " · выдан админом"
                                                                : ""}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="table-primary-cell">
                                                        <span className="table-primary-text">
                                                            {buildCustomerName(item)}
                                                        </span>
                                                        <span className="table-secondary-text">
                                                            {item.customer_info?.email ?? "—"}
                                                        </span>
                                                        {item.customer_info?.phone && (
                                                            <span className="table-secondary-text">
                                                                {item.customer_info.phone}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Город из адреса, указанного при оформлении;
                                                    для старых абонементов — из профиля клиента. */}
                                                <td>
                                                    <div className="table-primary-cell">
                                                        <span className="table-primary-text">
                                                            {item.city || item.customer_info?.city || "—"}
                                                        </span>
                                                        {(item.zip_code || item.address) && (
                                                            <span className="table-secondary-text">
                                                                {[item.zip_code, item.address]
                                                                    .filter(Boolean)
                                                                    .join(", ")}
                                                            </span>
                                                        )}
                                                        {item.country && (
                                                            <span className="table-secondary-text">
                                                                {item.country}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="table-primary-cell">
                                                        <span className="table-primary-text">
                                                            {formatDate(item.starts_at)} —{" "}
                                                            {formatDate(item.ends_at)}
                                                        </span>
                                                        {status === "active" && (
                                                            <span className="table-secondary-text">
                                                                осталось {item.days_left} дн.
                                                            </span>
                                                        )}
                                                        {item.is_canceled && (
                                                            <span
                                                                className="table-secondary-text"
                                                                style={{ color: "#de7878" }}
                                                            >
                                                                аннулирован{" "}
                                                                {formatDate(item.canceled_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <span className="table-primary-text">
                                                        {formatPercent(item.discount_percent)}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className="table-primary-cell">
                                                        <span className="table-primary-text">
                                                            {formatMoney(item.price)}
                                                        </span>
                                                        <span className="table-secondary-text">
                                                            {item.payment_type === "stripe"
                                                                ? "Stripe"
                                                                : "по счёту"}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <span
                                                        className="table-primary-text"
                                                        style={{ color: STATUS_COLORS[status] }}
                                                    >
                                                        {STATUS_LABELS[status]}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className="inline-actions">
                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Заказы клиента"
                                                            disabled={!item.customer_id && !item.customer_info?.email}
                                                            onClick={() => setItemOrders(item)}
                                                        >
                                                            <ListOrdered size={18} />
                                                        </button>

                                                        {canMarkPaid && (
                                                            <button
                                                                type="button"
                                                                className="icon-button"
                                                                title="Отметить оплаченным (активировать на год)"
                                                                disabled={isBusy}
                                                                onClick={() =>
                                                                    setItemToMarkPaid(item)
                                                                }
                                                            >
                                                                <CheckCircle2 size={18} />
                                                            </button>
                                                        )}

                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title={
                                                                item.is_paid
                                                                    ? "Отправить подтверждение на почту"
                                                                    : "Отправить счёт на почту"
                                                            }
                                                            disabled={isBusy}
                                                            onClick={() => handleSendInvoice(item)}
                                                        >
                                                            <Send size={18} />
                                                        </button>

                                                        {!item.is_paid && (
                                                            <button
                                                                type="button"
                                                                className="icon-button"
                                                                title="Отправить ссылку на оплату Stripe"
                                                                disabled={isBusy}
                                                                onClick={() =>
                                                                    handleSendPaymentLink(item)
                                                                }
                                                            >
                                                                <Link2 size={18} />
                                                            </button>
                                                        )}

                                                        <button
                                                            type="button"
                                                            className="icon-button"
                                                            title="Скачать счёт PDF"
                                                            disabled={isBusy}
                                                            onClick={() => handleDownloadPdf(item)}
                                                        >
                                                            <FileText size={18} />
                                                        </button>

                                                        {canAnnul && (
                                                            <button
                                                                type="button"
                                                                className="icon-button icon-button-danger"
                                                                title="Аннулировать (завершить сейчас)"
                                                                disabled={isBusy}
                                                                onClick={() => setItemToAnnul(item)}
                                                            >
                                                                <XCircle size={18} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="pagination-row">
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={page === 1}
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            >
                                Назад
                            </Button>

                            <span className="pagination-text">
                                Страница {page} из {totalPages} • всего {totalSize} абонементов
                            </span>

                            <Button
                                type="button"
                                variant="secondary"
                                disabled={page === totalPages}
                                onClick={() =>
                                    setPage((prev) => Math.min(totalPages, prev + 1))
                                }
                            >
                                Вперёд
                            </Button>
                        </div>
                    </>
                )}
            </Card>

            <AddSubscriptionModal
                isOpen={isAddOpen}
                onClose={() => setIsAddOpen(false)}
                onSubmit={handleCreate}
            />

            {itemToMarkPaid && (
                <ConfirmModal
                    isOpen={!!itemToMarkPaid}
                    title="Отметить абонемент оплаченным?"
                    description={`Абонемент ${itemToMarkPaid.subscription_number} будет активирован на год, клиент получит скидку ${formatPercent(
                        itemToMarkPaid.discount_percent,
                    )}.`}
                    confirmLabel="Отметить оплаченным"
                    cancelLabel="Отмена"
                    variant="primary"
                    onCancel={() => setItemToMarkPaid(null)}
                    onConfirm={handleConfirmMarkPaid}
                />
            )}

            {itemOrders && (
                <SubscriptionOrdersModal subscription={itemOrders} onClose={() => setItemOrders(null)} />
            )}

            {itemToAnnul && (
                <ConfirmModal
                    isOpen={!!itemToAnnul}
                    title="Аннулировать абонемент?"
                    description={`Дата завершения абонемента ${itemToAnnul.subscription_number} будет переписана на текущий момент, скидка перестанет действовать. Запись останется в списке.`}
                    confirmLabel="Аннулировать"
                    cancelLabel="Отмена"
                    variant="danger"
                    onCancel={() => setItemToAnnul(null)}
                    onConfirm={handleConfirmAnnul}
                />
            )}
        </div>
    );
};

export default SubscriptionsPage;
